import * as React from "react";
import { useRender } from "@base-ui/react/use-render";

/**
 * A drop-in replacement for `@radix-ui/react-slot`'s `Slot`, built on Base UI's
 * `useRender`.
 *
 * This exists so the three files that used Radix's `Slot` internally —
 * `button.tsx`, `breadcrumb.tsx`, `sidebar.tsx` — change only their import
 * line. All seven `const Comp = asChild ? Slot : "<tag>"` sites stay
 * byte-identical, which matters when `Button` alone has 3129 call sites.
 *
 * Base UI ships no `Slot` component; `useRender` is the primitive underneath
 * its `render` prop, and it does the same job: take the caller's element and
 * merge props onto it — composing event handlers, joining `className` and
 * `style`, and letting everything else overwrite.
 *
 * **Handler order is the one thing that needed pinning.** Radix composed as
 * `composeEventHandlers(childProps.onClick, slotProps.onClick)` — the child's
 * own handler runs first, then the wrapper's. The baseline asserts that order
 * rather than merely "both fired", because a caller that stops propagation in
 * its own handler behaves differently if the order flips.
 */
type SlotProps = {
  children?: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
} & Record<string, unknown>;

export function Slot({ children, ref, ...props }: SlotProps) {
  return useRender({
    // The caller's element *is* the rendered element — that is the whole point
    // of `asChild`. A `<button>` wrapping an `<a>` would be invalid HTML and
    // would break keyboard activation.
    render: React.isValidElement(children) ? children : undefined,
    props,
    ref,
  });
}
